"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import API from "../utils/api"
import "./AdjustmentManagement.css"
import { format } from "date-fns"

const AdjustmentManagement = () => {
  const [allProducts, setAllProducts] = useState([])
  const [products, setProducts] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchRef = useRef(null)

  const [adjustedByName, setAdjustedByName] = useState("")
  const [adjustmentDate, setAdjustmentDate] = useState(format(new Date(), "yyyy-MM-dd"))
  const [notes, setNotes] = useState("")
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [selectedAdjustments, setSelectedAdjustments] = useState([])

  const totalIncrease = useMemo(
    () => selectedAdjustments.filter(a => a.action === "increase").reduce((sum, a) => sum + (Number(a.quantity) || 0), 0),
    [selectedAdjustments]
  )
  const totalDecrease = useMemo(
    () => selectedAdjustments.filter(a => a.action === "decrease").reduce((sum, a) => sum + (Number(a.quantity) || 0), 0),
    [selectedAdjustments]
  )

  useEffect(() => {
    fetchAllProducts()

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const fetchAllProducts = async () => {
    try {
      const res = await API.get("/products")
      setAllProducts(res.data || [])
    } catch (err) {
      console.error("Error fetching products:", err)
      setMessage("❌ Error fetching products")
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch()
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleSearch = () => {
    const query = (searchQuery || "").trim().toLowerCase()
    if (!query) {
      setProducts([])
      setShowSearchResults(false)
      return
    }

    setIsSearching(true)
    try {
      const filtered = (allProducts || []).filter((p) =>
        (p.companyName || "").toLowerCase().includes(query) ||
        (p.modelNo || "").toLowerCase().includes(query) ||
        (p.invoiceNo || "").toLowerCase().includes(query) ||
        (p.size || "").toLowerCase().includes(query) ||
        (p.color || "").toLowerCase().includes(query)
      )
      setProducts(filtered)
      setShowSearchResults(true)
    } finally {
      setIsSearching(false)
    }
  }

  const addAdjustment = (product, action, quantity) => {
    const qty = Number.parseInt(quantity)
    if (!qty || qty <= 0) {
      setMessage("⚠️ Please enter valid quantity")
      return
    }
    const existingIndex = selectedAdjustments.findIndex((a) => a._id === product._id)
    const nextItem = {
      ...product,
      action,
      quantity: qty,
      adjustedBy: adjustedByName,
      date: adjustmentDate,
      notes,
    }
    if (existingIndex >= 0) {
      const copy = [...selectedAdjustments]
      copy[existingIndex] = nextItem
      setSelectedAdjustments(copy)
      setMessage(`✅ Updated ${action === "increase" ? "increase" : "decrease"} for ${product.companyName} - ${product.modelNo}`)
    } else {
      setSelectedAdjustments([...selectedAdjustments, nextItem])
      setMessage(`✅ Added ${product.companyName} - ${product.modelNo} to adjustment list`)
    }
    setShowSearchResults(false)
  }

  const removeAdjustment = (productId) => {
    setSelectedAdjustments((prev) => prev.filter((a) => a._id !== productId))
  }

  const processAdjustments = async () => {
    if (selectedAdjustments.length === 0) {
      setMessage("⚠️ Please add products to adjustment list first.")
      return
    }
    if (!adjustedByName.trim()) {
      setMessage("⚠️ Please enter Name (Adjusted By).")
      return
    }

    setIsSubmitting(true)
    setMessage("🔄 Processing adjustments...")
    try {
      const requests = selectedAdjustments.map((item) => {
        const qty = Number(item.quantity)
        return API.post("/products/adjustments", {
          productId: item._id,
          action: String(item.action).toLowerCase(),
          quantity: qty,
          adjustedBy: adjustedByName,
          date: adjustmentDate,
          notes: notes || "",
          companyName: item.companyName,
          modelNo: item.modelNo,
          size: item.size || null,
        })
      })
      await Promise.all(requests)

      setMessage(`✅ Adjusted ${selectedAdjustments.length} product(s) by ${adjustedByName}`)
      setSelectedAdjustments([])
      setNotes("")
      setSearchQuery("")
      setProducts([])
      setShowSearchResults(false)
      fetchAllProducts()
    } catch (err) {
      console.error("Adjustment error:", err)
      const errorMsg = err?.response?.data?.message || err.message || "Error processing adjustments"
      setMessage(`❌ ${errorMsg}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="adjustment-management-container">
      <div className="page-header">
        <h2>
          <i className="fas fa-sliders-h"></i> Adjustment
        </h2>
      </div>

      <div className="adjustment-entry-section">
        <h3>
          <i className="fas fa-tools"></i> Increase / Decrease Product Quantity
        </h3>

        <div className="adjuster-info-section">
          <h4>
            <i className="fas fa-user"></i> Name & Date
          </h4>
          <div className="adjuster-form">
            <input
              type="text"
              placeholder="Name (Adjusted By)"
              value={adjustedByName}
              onChange={(e) => setAdjustedByName(e.target.value)}
              className="adjuster-input"
            />
            <input
              type="date"
              value={adjustmentDate}
              onChange={(e) => setAdjustmentDate(e.target.value)}
              max={format(new Date(), "yyyy-MM-dd")}
              className="date-input"
            />
            <input
              type="text"
              placeholder="Reason/Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="notes-input"
            />
          </div>
        </div>

        {selectedAdjustments.length > 0 && (
          <div className="adjustment-list">
            <h4>
              <i className="fas fa-clipboard-list"></i> Adjustment List ({selectedAdjustments.length} items, +{totalIncrease} / -{totalDecrease})
            </h4>
            <div className="adjustment-items">
              {selectedAdjustments.map((item) => (
                <div key={item._id} className="adjustment-item">
                  <div className="adjustment-item-info">
                    <strong>{item.companyName}</strong> - {item.modelNo}
                    <span className="adjustment-item-details">
                      {item.action === "increase" ? (
                        <span className="inc">+{item.quantity}</span>
                      ) : (
                        <span className="dec">-{item.quantity}</span>
                      )}
                    </span>
                  </div>
                  <button className="remove-btn" onClick={() => removeAdjustment(item._id)}>
                    <i className="fas fa-times"></i> Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="adjustment-actions">
              <button
                onClick={processAdjustments}
                disabled={isSubmitting || !adjustedByName.trim()}
                className="process-adjustments-btn"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle"></i> Save Adjustments for {adjustedByName || "Name"}
                  </>
                )}
              </button>
              <button className="clear-list-btn" onClick={() => setSelectedAdjustments([])}>
                <i className="fas fa-trash"></i> Clear List
              </button>
            </div>
          </div>
        )}

        <div className="search-container" ref={searchRef}>
          <h4>
            <i className="fas fa-search"></i> Search & Adjust Products
          </h4>
          <div className="search-bar-wrapper">
            <input
              type="text"
              placeholder="Search by product/model/invoice/size/color"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setShowSearchResults(true)
              }}
              onFocus={() => setShowSearchResults(true)}
              className="search-input"
            />
            <button className="search-button" onClick={handleSearch} disabled={isSearching}>
              {isSearching ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-search"></i>}
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>

          {showSearchResults && products.length > 0 && (
            <div className="search-results-dropdown">
              <div className="search-results-header">
                <span>
                  <i className="fas fa-list"></i> Found {products.length} products
                </span>
                <button className="close-results-btn" onClick={() => setShowSearchResults(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="search-results-list">
                {products.map((p) => (
                  <div key={p._id} className="search-result-item">
                    <div className="search-result-info">
                      <div className="search-result-main">
                        <strong>{p.companyName}</strong> - {p.modelNo}
                        {p.invoiceNo && <span className="invoice-no"> (Invoice: {p.invoiceNo})</span>}
                      </div>
                      <div className="search-result-details">
                        {p.size && (
                          <span>
                            <i className="fas fa-ruler"></i> Size: {p.size}
                          </span>
                        )}
                        {p.color && (
                          <span>
                            <i className="fas fa-palette"></i> Color: {p.color}
                          </span>
                        )}
                        <span className="product-stock">
                          <i className="fas fa-boxes"></i> Current Stock: {p.quantity} pcs
                        </span>
                        <span className="product-alert">
                          <i className="fas fa-bell"></i> Alert Qty: {p.alertQty}
                        </span>
                      </div>
                    </div>
                    <div className="search-result-actions">
                      <div className="input-with-icon">
                        <i className="fas fa-cubes"></i>
                        <input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          className="qty-input"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const qty = Number.parseInt(e.currentTarget.value)
                              if (qty > 0) {
                                addAdjustment(p, "increase", qty)
                                e.currentTarget.value = ""
                              }
                            }
                          }}
                        />
                      </div>
                      <button
                        className="increase-btn"
                        title="Increase"
                        onClick={(e) => {
                          const qtyInput = e.currentTarget.parentElement.querySelector(".qty-input")
                          const qty = Number.parseInt(qtyInput.value)
                          if (qty > 0) {
                            addAdjustment(p, "increase", qty)
                            qtyInput.value = ""
                          } else {
                            setMessage("⚠️ Please enter valid quantity")
                          }
                        }}
                      >
                        <i className="fas fa-plus"></i> Increase
                      </button>
                      <button
                        className="decrease-btn"
                        title="Decrease"
                        onClick={(e) => {
                          const qtyInput = e.currentTarget.parentElement.querySelector(".qty-input")
                          const qty = Number.parseInt(qtyInput.value)
                          if (qty > 0) {
                            addAdjustment(p, "decrease", qty)
                            qtyInput.value = ""
                          } else {
                            setMessage("⚠️ Please enter valid quantity")
                          }
                        }}
                      >
                        <i className="fas fa-minus"></i> Decrease
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showSearchResults && searchQuery && products.length === 0 && !isSearching && (
            <div className="search-results-dropdown">
              <div className="no-search-results">
                <p>
                  <i className="fas fa-search"></i> No products found for "{searchQuery}"
                </p>
              </div>
            </div>
          )}
        </div>

        {message && (
          <p
            className={`message ${
              message.startsWith("❌") ? "error" : message.startsWith("⚠️") ? "warning" : "success"
            }`}
          >
            {message.startsWith("❌") && <i className="fas fa-exclamation-circle"></i>}
            {message.startsWith("⚠️") && <i className="fas fa-exclamation-triangle"></i>}
            {message.startsWith("✅") && <i className="fas fa-check-circle"></i>}
            {message}
          </p>
        )}
      </div>
    </div>
  )
}

export default AdjustmentManagement

